'use client'

import { getImageProps } from 'next/image'
import * as GridLayout from '@/components/GridLayout/GridLayout'
import { DEFAULT_IMAGE_QUALITY } from '@/utils/config'
import { Heading } from 'ui/src/components/Heading/Heading'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { HowToBuyDialog } from '../../HowToBuyDialog/HowToBuyDialog'
import stampLogoDesktop from './stamp-logo.png'
import stampLogoMobile from './stamp-logo-mobile.png'
import vintageShopHeroBg from './vintage-shop-hero-light.jpg'
import vintageShopHeroBgMobile from './vintage-shop-hero-mobile-light.jpg'
import {
  backgroundContent,
  backgroundImageStyle,
  backgroundRoot,
  headingContainer,
  stampLogo,
} from './VintageShopHero.css'

type VintageShopHeroProps = {
  heading?: string
  buttonLabel?: string
  hasBackgroundImage?: boolean
}

export function VintageShopHero({ heading, buttonLabel, hasBackgroundImage }: VintageShopHeroProps) {
  const {
    props: { srcSet: stampLogoDesktopSrcSet },
  } = getImageProps({
    alt: 'The Vintage Furniture Sale',
    quality: DEFAULT_IMAGE_QUALITY,
    src: stampLogoDesktop,
    width: 2000,
    height: 205,
    sizes: '(min-width: 768px) min(43rem, 100vw)',
  })

  const {
    props: { alt: stampLogoAlt, ...rest },
  } = getImageProps({
    alt: 'The Vintage Furniture Sale',
    quality: DEFAULT_IMAGE_QUALITY,
    src: stampLogoMobile,
    width: 1000,
    height: 296,
    sizes: '(max-width: 767px) 17.625rem, 100vw',
    loading: 'eager',
    fetchPriority: 'high',
  })

  const heroContent = (
    <GridLayout.Root className={sprinkles({ marginTop: 'xxxl', marginBottom: 'xxl' })}>
      <GridLayout.Content width={{ _: '1', lg: '2/3' }} align="center">
        <section className={yStack({ gap: { _: 'xl', md: 'xxl' }, paddingTop: 'xxxl', paddingBottom: 'xxxl' })}>
          <div className={yStack({ gap: { _: 'sm', md: 'xs' } })}>
            <div className={sprinkles({ display: 'flex', justifyContent: 'center', width: '100%' })}>
              <picture>
                <source media="(min-width: 768px)" srcSet={stampLogoDesktopSrcSet} />
                <img alt={stampLogoAlt} {...rest} className={stampLogo} />
              </picture>
            </div>
            <Heading
              as="h1"
              variant={{ _: 'standard.14', md: 'standard.18' }}
              align="center"
              balance={true}
              className={headingContainer}
            >
              {heading}
            </Heading>
          </div>

          <div className={xStack({ gap: 'sm', justifyContent: 'center' })}>
            <HowToBuyDialog buttonLabel={buttonLabel} />
          </div>
        </section>
      </GridLayout.Content>
    </GridLayout.Root>
  )

  const {
    props: { srcSet: desktopBgSrcSet },
  } = getImageProps({
    alt: '',
    src: vintageShopHeroBg,
    quality: DEFAULT_IMAGE_QUALITY,
    sizes: '100vw',
    priority: true,
  })

  const {
    props: { alt: bgAlt, ...bgRest },
  } = getImageProps({
    alt: '',
    src: vintageShopHeroBgMobile,
    quality: DEFAULT_IMAGE_QUALITY,
    sizes: '100vw',
    priority: true,
  })

  if (hasBackgroundImage) {
    return (
      <div className={backgroundRoot}>
        <picture>
          <source media="(min-width: 1024px)" srcSet={desktopBgSrcSet} />
          <img alt={bgAlt} {...bgRest} className={backgroundImageStyle} />
        </picture>
        <div className={backgroundContent}>{heroContent}</div>
      </div>
    )
  }

  return heroContent
}
